import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { Printer } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Bill } from '@/models/bill/bill'
import { formatNumber } from '@/utils/number'
import { printBillPdf } from '@/modules/bill/services/bill'

import { BillPDF, type BillPdfHandle } from './BillPDF'
import { BillPdfRoundPreview } from './BillPdfRoundPreview'

type BillInstallmentPdfPanelProps = {
  data: Bill
  handlePrintBill: () => void
  selectedRoundNo?: number
  onSelectedRoundChange?: (roundNo: number) => void
}

export const BillInstallmentPdfPanel = forwardRef<BillPdfHandle, BillInstallmentPdfPanelProps>(
  function BillInstallmentPdfPanel(
    { data, handlePrintBill, selectedRoundNo, onSelectedRoundChange },
    ref,
  ) {
    const singlePdfRef = useRef<BillPdfHandle>(null)

    const rounds = useMemo(
      () => [...(data.payment_rounds ?? [])].sort((a, b) => a.round_no - b.round_no),
      [data.payment_rounds],
    )

    const latestRound = rounds[rounds.length - 1]?.round_no
    const activeRoundNo = selectedRoundNo ?? latestRound
    const activeRound = rounds.find((r) => r.round_no === activeRoundNo) ?? rounds[rounds.length - 1]

    useEffect(() => {
      if (latestRound != null && selectedRoundNo == null) {
        onSelectedRoundChange?.(latestRound)
      }
    }, [latestRound, onSelectedRoundChange, selectedRoundNo])

    useImperativeHandle(
      ref,
      () => ({
        print: async () => {
          const round = activeRoundNo ?? latestRound
          if (round != null) {
            await printBillPdf(data.id, round)
            handlePrintBill()
            return
          }
          await singlePdfRef.current?.print()
        },
        printRound: async (roundNo: number) => {
          onSelectedRoundChange?.(roundNo)
          await printBillPdf(data.id, roundNo)
          handlePrintBill()
        },
        selectRound: (roundNo: number) => {
          onSelectedRoundChange?.(roundNo)
        },
        getSelectedRound: () => activeRoundNo,
      }),
      [activeRoundNo, data.id, handlePrintBill, latestRound, onSelectedRoundChange],
    )

    if (rounds.length === 0) {
      return (
        <BillPDF
          ref={singlePdfRef}
          data={data}
          handlePrintBill={handlePrintBill}
          hideInlinePrintButton
        />
      )
    }

    const tabValue = activeRound ? String(activeRound.round_no) : String(rounds[0].round_no)

    return (
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-muted-foreground">
            ใบเสร็จรับเงิน ({rounds.length} งวด) — เลือกแท็บเพื่อดูแต่ละงวด
          </p>
          {activeRound ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full shrink-0 sm:w-auto"
              onClick={() => void printBillPdf(data.id, activeRound.round_no)}
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              พิมพ์งวดที่ {activeRound.round_no}
            </Button>
          ) : null}
        </div>

        <Tabs
          value={tabValue}
          onValueChange={(v) => onSelectedRoundChange?.(Number(v))}
          className="w-full"
        >
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/40 p-1">
            {rounds.map((r) => (
              <TabsTrigger
                key={r.id}
                value={String(r.round_no)}
                className="h-8 px-2.5 text-xs data-[state=active]:bg-background"
              >
                งวด {r.round_no}
                <span className="ml-1 text-muted-foreground">({formatNumber(r.paid_amount)})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {rounds.map((round) => (
            <TabsContent key={round.id} value={String(round.round_no)} className="mt-3 focus-visible:outline-none">
              <BillPdfRoundPreview billId={data.id} round={round} compact />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    )
  },
)

BillInstallmentPdfPanel.displayName = 'BillInstallmentPdfPanel'
